/**
 * Asking a teacher for a private, and reading where it stands (D-286, D-553).
 *
 * Only the family's door is here: asking, and the list. Her answer, her offer
 * of another time, the family's answer to that and taking a request back all
 * go through the ask envelope (`api/asks.ts`), which is why the teacher's own
 * door of D-290 no longer exists.
 */

/**
 * Where a request stands, worked out on the server from the booking and the
 * asks on the envelope (D-286, D-553). Never stored, so it cannot drift.
 */
export type PrivateRequestState =
  | 'AwaitingTeacher'
  | 'AwaitingParent'
  | 'Accepted'
  | 'Declined'
  | 'Withdrawn'
  | 'Expired'
  /** It was agreed and then called off, by either side (D-690). */
  | 'Cancelled'

export type PrivateRequest = {
  id: string
  dancerId: string
  dancerName: string
  teacherStaffId: string
  teacherName: string
  state: PrivateRequestState
  requestedDate: string
  requestedStart: string
  requestedEnd: string
  proposedDate: string | null
  proposedStart: string | null
  proposedEnd: string | null
  note: string | null
  declineReason: string | null
  /** The class this repairs. Null for an ordinary private. */
  repairsClassName: string | null
  repairsDate: string | null
}

/**
 * A list, or an error - never something a caller has to guard against.
 *
 * <b>Written after the panel took the whole portal down with it.</b> A response
 * that was not an array reached `.filter` and threw inside render, so the
 * schedule a parent actually came for went blank because a side panel could not
 * read its own data. D-209 is explicit that one request failing while another
 * succeeds is the normal case rather than the rare one — throwing here puts it
 * in the panel's own error state, where it says so and offers a retry.
 */
function listOf<T>(data: unknown, what: string): T[] {
  if (Array.isArray(data)) return data as T[]
  throw new Error(`Could not read ${what}.`)
}

async function ok(res: Response, fallback: string): Promise<void> {
  if (res.ok) return

  const problem = await res.json().catch(() => null)
  throw new Error(problem?.error ?? fallback)
}

// --- the family's door ------------------------------------------------------

export type AskableTeacher = { id: string; name: string }

/**
 * Who this family may ask, derived from enrolments on the server.
 *
 * Not a staff directory: a teacher who has never met this dancer is not
 * somebody to ask for a private, which is the same rule M3 sets for threads.
 */
export async function fetchAskableTeachers(): Promise<AskableTeacher[]> {
  const res = await fetch('/api/portal/private-requests/teachers')
  if (!res.ok) throw new Error('Could not load your teachers.')
  return listOf<AskableTeacher>(await res.json(), 'your teachers')
}

export async function fetchMyPrivateRequests(): Promise<PrivateRequest[]> {
  const res = await fetch('/api/portal/private-requests')
  if (!res.ok) throw new Error('Could not load your private lesson requests.')
  return listOf<PrivateRequest>(await res.json(), 'your private lessons')
}

export type AskForPrivate = {
  dancerId: string
  teacherStaffId: string
  date: string
  start: string
  end: string
  /** The missed solo or duet this would make up. Omit for an ordinary private. */
  repairsOccurrenceId?: string | null
  note?: string | null
}

/**
 * The request and the ask it was raised as (D-553): a conversation that opened
 * this door pins the ask (D-630), and the panel keys its rows on the request.
 */
export async function askForPrivate(body: AskForPrivate): Promise<{ requestId: string; askId: string }> {
  const res = await fetch('/api/portal/private-requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  await ok(res, 'Could not send that request. Please try again.')
  const raised = (await res.json()) as { requestId: string; askId: string }
  return { requestId: raised.requestId, askId: raised.askId }
}
